import { Box, Button, Chip, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useMediaQuery, useTheme } from '@mui/material';
import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import type { AppError } from '../../api/contract';
import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { countActiveFilterValues } from '../../shared/filter/activeFilterCount';
import { ListFilterPanel } from '../../shared/filter/ListFilterPanel';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { PageHeader } from '../../shared/layout/PageHeader';
import { defaultTransactionListQuery, transactionRepositoryToken } from './repository';
import type { TransactionFilters, TransactionListQuery, TransactionSummary } from './types';
import { formatTransactionMoney } from './types';

const outcomes = new Set(['WIN', 'LOSS', 'DRAW']);
const statuses = new Set(['CALCULATED', 'REJECTED', 'FAILED']);
const reversalStates = new Set(['NOT_REVERSED', 'REVERSED']);
const sorts = new Set(['receivedAt', 'playAt']);
const offsetTimePattern =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|[+-]\d{2}:\d{2})$/u;
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;
const currencyPattern = /^[A-Za-z]{3}$/u;
const errorCodePattern = /^[A-Za-z][A-Za-z0-9_]{1,99}$/u;
const unsignedFinancialDecimalPattern = /^(?:0|[1-9][0-9]{0,19})(?:\.[0-9]{1,6})?$/u;

export function TransactionListPage({ dataSource }: { readonly dataSource: DataSource }) {
  const repository = useRepository(transactionRepositoryToken);
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const scope = auth.status === 'authenticated' ? auth.principal.scope : null;
  const [query, setQuery] = useState(() =>
    initialTransactionQuery(searchParams, scope?.partnerId ?? null, scope?.agentId ?? null),
  );
  const [cursorHistory, setCursorHistory] = useState<readonly string[]>(['']);
  const [pageIndex, setPageIndex] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const resource = useResource(
    (signal) => repository.list(query, signal),
    [query, refreshKey, repository],
    (page) => page.items.length === 0 && query.cursor === '',
  );
  const columns = useMemo<readonly GridColDef<TransactionSummary>[]>(
    () => transactionColumns(language, t),
    [language, t],
  );

  const resetCursor = (filters: TransactionFilters, sort: TransactionListQuery['sort']) => {
    setCursorHistory(['']);
    setPageIndex(0);
    setQuery((current) => ({ ...current, cursor: '', filters, sort }));
  };
  const next = () => {
    const cursor = resource.data?.nextCursor;
    if (cursor === null || cursor === undefined) return;
    const index = pageIndex + 1;
    setCursorHistory((current) => [...current.slice(0, index), cursor]);
    setPageIndex(index);
    setQuery((current) => ({ ...current, cursor }));
  };
  const previous = () => {
    if (pageIndex === 0) return;
    const index = pageIndex - 1;
    setPageIndex(index);
    setQuery((current) => ({ ...current, cursor: cursorHistory[index] ?? '' }));
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        actions={<DataSourceBadge source={dataSource} />}
        subtitle={t('transactions.subtitle')}
        title={t('transactions.title')}
      />
      <TransactionFilterPanel
        agentLocked={scope?.agentId !== null && scope?.agentId !== undefined}
        onApply={resetCursor}
        partnerLocked={scope?.partnerId !== null && scope?.partnerId !== undefined}
        value={query}
      />
      <PageState
        emptyMessage={t('transactions.empty')}
        error={toTransactionPageError(resource.error, t('transactions.errors.list'))}
        onRetry={() => setRefreshKey((value) => value + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <Stack spacing={1}>
            {mobile ? (
              <CompactTransactions items={resource.data.items} language={language} />
            ) : (
              <Box
                aria-label={t('transactions.gridLabel')}
                role="region"
                sx={{ maxWidth: '100%', overflowX: 'auto' }}
                tabIndex={0}
              >
                <Box sx={{ height: 430, minWidth: 1450 }}>
                  <DataGrid
                    aria-label={t('transactions.gridLabel')}
                    columns={columns}
                    disableRowSelectionOnClick
                    hideFooter
                    rows={resource.data.items}
                    sortingMode="server"
                  />
                </Box>
              </Box>
            )}
            <Typography color="text.secondary" variant="caption">
              {t('transactions.serverOrder')}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
              <Button disabled={pageIndex === 0} onClick={previous}>
                {t('transactions.pagination.previous')}
              </Button>
              <Button disabled={resource.data.nextCursor === null} onClick={next}>
                {t('transactions.pagination.next')}
              </Button>
            </Stack>
          </Stack>
        )}
      </PageState>
    </Box>
  );
}

function CompactTransactions({
  items,
  language,
}: {
  readonly items: readonly TransactionSummary[];
  readonly language: TimeLanguage;
}) {
  const { t } = useTranslation();
  return (
    <Stack
      aria-label={t('transactions.compactResultsLabel')}
      component="ul"
      spacing={1}
      sx={{ listStyle: 'none', m: 0, p: 0 }}
    >
      {items.map((item) => (
        <Paper component="li" key={item.id} sx={{ p: 2 }} variant="outlined">
          <Stack spacing={1}>
            <Stack direction="row" spacing={1}>
              <Chip label={item.persistedStatus} size="small" />
              <Chip label={item.reversalState} size="small" variant="outlined" />
            </Stack>
            <Link to={`${paths.transactions}/${encodeURIComponent(item.id)}`}>
              {item.partnerTransactionId}
            </Link>
            <Typography>
              {item.partner.code} · {item.submittedUsername}
            </Typography>
            <Typography>{formatTransactionMoney(item)}</Typography>
            <Typography color="text.secondary" variant="caption">
              {formatTimestamp(item.receivedAt, 'Asia/Bangkok', language).display}
            </Typography>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

function TransactionFilterPanel({
  agentLocked,
  onApply,
  partnerLocked,
  value,
}: {
  readonly agentLocked: boolean;
  readonly onApply: (filters: TransactionFilters, sort: TransactionListQuery['sort']) => void;
  readonly partnerLocked: boolean;
  readonly value: TransactionListQuery;
}) {
  const { t } = useTranslation();
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const read = (key: keyof TransactionFilters) => String(data.get(key) ?? '').trim();
    const filters = Object.fromEntries(
      Object.keys(defaultTransactionListQuery.filters).map((key) => [
        key,
        read(key as keyof TransactionFilters),
      ]),
    ) as unknown as TransactionFilters;
    const writable = filters as unknown as Record<keyof TransactionFilters, string>;
    if (partnerLocked) writable.partnerId = value.filters.partnerId;
    if (agentLocked) writable.agentId = value.filters.agentId;
    onApply(normalizeFilters(filters), String(data.get('sort')) as TransactionListQuery['sort']);
  };
  const primaryTextFields = ['partnerTransactionId', 'username'] as const;
  const secondaryTextFields = [
    'partnerId',
    'agentId',
    'memberId',
    'partnerUserId',
    'uplineMemberId',
    'uplineUsername',
    'gameTypeId',
    'gameTypeCode',
    'gameId',
    'gameCode',
    'gameRound',
    'currency',
    'errorCode',
    'minimumAmount',
    'maximumAmount',
    'playFrom',
    'playTo',
    'receivedFrom',
    'receivedTo',
  ] as const;
  const scopedDefaults = {
    ...defaultTransactionListQuery.filters,
    agentId: agentLocked ? value.filters.agentId : '',
    partnerId: partnerLocked ? value.filters.partnerId : '',
  };
  const activeSecondaryFilterCount = countActiveFilterValues(
    {
      ...Object.fromEntries(secondaryTextFields.map((key) => [key, value.filters[key]])),
      reversalState: value.filters.reversalState,
      sort: value.sort,
    },
    {
      ...Object.fromEntries(secondaryTextFields.map((key) => [key, scopedDefaults[key]])),
      reversalState: scopedDefaults.reversalState,
      sort: defaultTransactionListQuery.sort,
    },
  );
  return (
    <ListFilterPanel
      activeSecondaryFilterCount={activeSecondaryFilterCount}
      applyLabel={t('transactions.actions.applyFilters')}
      ariaLabel={t('filterPanel.label', { page: t('transactions.title') })}
      key={`${value.sort}:${JSON.stringify(value.filters)}`}
      onClear={() =>
        onApply(
          {
            ...defaultTransactionListQuery.filters,
            partnerId: partnerLocked ? value.filters.partnerId : '',
            agentId: agentLocked ? value.filters.agentId : '',
          },
          defaultTransactionListQuery.sort,
        )
      }
      onSubmit={submit}
      primaryFields={
        <>
          {primaryTextFields.map((key) => (
            <TextField
              defaultValue={value.filters[key]}
              key={key}
              label={t(`transactions.fields.${key}`)}
              name={key}
              size="small"
            />
          ))}
          <SelectFilter
            label={t('transactions.fields.status')}
            name="status"
            options={['CALCULATED', 'REJECTED', 'FAILED']}
            value={value.filters.status}
          />
          <SelectFilter
            label={t('transactions.fields.outcome')}
            name="outcome"
            options={['WIN', 'LOSS', 'DRAW']}
            value={value.filters.outcome}
          />
        </>
      }
      secondaryFields={
        <>
          {secondaryTextFields.map((key) => (
            <TextField
              defaultValue={value.filters[key]}
              disabled={
                (key === 'partnerId' && partnerLocked) || (key === 'agentId' && agentLocked)
              }
              key={key}
              label={t(`transactions.fields.${key}`)}
              name={key}
              size="small"
            />
          ))}
          <SelectFilter
            label={t('transactions.fields.reversalState')}
            name="reversalState"
            options={['NOT_REVERSED', 'REVERSED']}
            value={value.filters.reversalState}
          />
          <SelectFilter
            label={t('transactions.fields.sort')}
            name="sort"
            options={['receivedAt', 'playAt']}
            value={value.sort}
            includeAll={false}
          />
        </>
      }
    />
  );
}

function SelectFilter({
  includeAll = true,
  label,
  name,
  options,
  value,
}: {
  readonly includeAll?: boolean;
  readonly label: string;
  readonly name: string;
  readonly options: readonly string[];
  readonly value: string;
}) {
  const { t } = useTranslation();
  return (
    <TextField defaultValue={value} label={label} name={name} select size="small">
      {includeAll ? <MenuItem value="">{t('common.all')}</MenuItem> : null}
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          {option}
        </MenuItem>
      ))}
    </TextField>
  );
}

function transactionColumns(
  language: TimeLanguage,
  t: ReturnType<typeof useTranslation>['t'],
): readonly GridColDef<TransactionSummary>[] {
  return [
    {
      field: 'partnerTransactionId',
      headerName: t('transactions.fields.partnerTransactionId'),
      minWidth: 210,
      sortable: false,
      renderCell: ({ row }) => (
        <Link to={`${paths.transactions}/${encodeURIComponent(row.id)}`}>
          {row.partnerTransactionId}
        </Link>
      ),
    },
    {
      field: 'partner',
      headerName: t('transactions.fields.partner'),
      minWidth: 150,
      sortable: false,
      valueGetter: (_value, row) => row.partner.code,
    },
    {
      field: 'submittedUsername',
      headerName: t('transactions.fields.username'),
      minWidth: 150,
      sortable: false,
    },
    {
      field: 'submittedGameCode',
      headerName: t('transactions.fields.gameCode'),
      minWidth: 130,
      sortable: false,
    },
    {
      field: 'actualPlayAmount',
      headerName: t('transactions.fields.actualPlayAmount'),
      minWidth: 160,
      sortable: false,
      valueGetter: (_value, row) => formatTransactionMoney(row),
    },
    {
      field: 'outcome',
      headerName: t('transactions.fields.outcome'),
      minWidth: 100,
      sortable: false,
    },
    {
      field: 'persistedStatus',
      headerName: t('transactions.fields.status'),
      minWidth: 130,
      sortable: false,
    },
    {
      field: 'reversalState',
      headerName: t('transactions.fields.reversalState'),
      minWidth: 150,
      sortable: false,
    },
    {
      field: 'failure',
      headerName: t('transactions.fields.errorCode'),
      minWidth: 170,
      sortable: false,
      valueGetter: (_value, row) => row.failure?.code ?? '—',
    },
    {
      field: 'playAt',
      headerName: t('transactions.fields.playAt'),
      minWidth: 190,
      sortable: false,
      valueGetter: (_value, row) => formatTimestamp(row.playAt, 'Asia/Bangkok', language).display,
    },
    {
      field: 'receivedAt',
      headerName: t('transactions.fields.receivedAt'),
      minWidth: 190,
      sortable: false,
      valueGetter: (_value, row) =>
        formatTimestamp(row.receivedAt, 'Asia/Bangkok', language).display,
    },
  ];
}

function normalizeFilters(filters: TransactionFilters): TransactionFilters {
  return Object.fromEntries(
    Object.entries(filters).map(([key, value]) => [
      key,
      ['currency', 'gameTypeCode', 'gameCode', 'errorCode'].includes(key)
        ? value.trim().toUpperCase()
        : value.trim(),
    ]),
  ) as unknown as TransactionFilters;
}

export function initialTransactionQuery(
  searchParams: URLSearchParams,
  scopedPartnerId: string | null,
  scopedAgentId: string | null,
): TransactionListQuery {
  const filters = { ...defaultTransactionListQuery.filters };
  const writable = filters as unknown as Record<keyof TransactionFilters, string>;
  for (const key of Object.keys(filters) as readonly (keyof TransactionFilters)[])
    writable[key] = searchParams.get(key)?.trim() ?? '';
  for (const key of [
    'partnerId',
    'agentId',
    'memberId',
    'uplineMemberId',
    'gameTypeId',
    'gameId',
  ] as const)
    filters[key] = validOrEmpty(filters[key], uuidPattern);
  for (const key of [
    'username',
    'partnerUserId',
    'uplineUsername',
    'gameRound',
    'partnerTransactionId',
  ] as const)
    filters[key] = boundedOrEmpty(filters[key], 200);
  for (const key of ['gameTypeCode', 'gameCode'] as const)
    filters[key] = boundedOrEmpty(filters[key], 64);
  filters.outcome = outcomes.has(filters.outcome) ? filters.outcome : '';
  filters.status = statuses.has(filters.status) ? filters.status : '';
  filters.reversalState = reversalStates.has(filters.reversalState) ? filters.reversalState : '';
  for (const key of ['playFrom', 'playTo', 'receivedFrom', 'receivedTo'] as const)
    filters[key] = validFinancialTimestampOrEmpty(filters[key]);
  filters.currency = validOrEmpty(filters.currency, currencyPattern);
  filters.errorCode = validOrEmpty(filters.errorCode, errorCodePattern);
  filters.minimumAmount = validOrEmpty(filters.minimumAmount, unsignedFinancialDecimalPattern);
  filters.maximumAmount = validOrEmpty(filters.maximumAmount, unsignedFinancialDecimalPattern);
  filters.currency = filters.currency.toUpperCase();
  filters.gameTypeCode = filters.gameTypeCode.toUpperCase();
  filters.gameCode = filters.gameCode.toUpperCase();
  filters.errorCode = filters.errorCode.toUpperCase();
  if (scopedPartnerId !== null) filters.partnerId = scopedPartnerId;
  if (scopedAgentId !== null) filters.agentId = scopedAgentId;
  const requestedSort = searchParams.get('sort') ?? '';
  return {
    ...defaultTransactionListQuery,
    sort: sorts.has(requestedSort)
      ? (requestedSort as TransactionListQuery['sort'])
      : defaultTransactionListQuery.sort,
    filters,
  };
}

export function toTransactionPageError(
  error: unknown,
  fallback: string,
): Pick<AppError, 'message' | 'requestId'> | null {
  if (error === null || error === undefined) return null;
  if (typeof error === 'object' && error !== null) {
    const candidate = error as { readonly requestId?: unknown };
    return {
      message: fallback,
      requestId: typeof candidate.requestId === 'string' ? candidate.requestId : null,
    };
  }
  return { message: fallback, requestId: null };
}

function validOrEmpty(value: string, pattern: RegExp): string {
  return value === '' || pattern.test(value) ? value : '';
}

function boundedOrEmpty(value: string, maximum: number): string {
  return value === '' || (value.length >= 1 && value.length <= maximum) ? value : '';
}

function validFinancialTimestampOrEmpty(value: string): string {
  if (value === '' || !offsetTimePattern.test(value)) return '';
  try {
    formatTimestamp(value);
    return value;
  } catch {
    return '';
  }
}
